import { ColumnSizingState, VisibilityState } from '@tanstack/react-table';

const LIST_OF_STORAGES = [
  'email-template',
  'operations-list-withdrawal',
  'operations-list-deposit',
  'company-account',
  'company-transfer',
  'contest',
  'contest-participant',
  'contest-document',
  'contest-trading-account-type',
  'contest-user-restriction',
  'loyalty-program',
  'loyalty-program-document',
  'loyalty-program-participant',
  'loyalty-program-processor',
  'loyalty-program-redeem',
  'loyalty-program-operation',
  'loyalty-program-participant-redeem',
  'trading-platform-branding-settings',
  'legal-trading-platform',
  'legal-document',
  'legal-file-item',
  'faq',
  'url-domain',
  'short-link',
  'client-side-translation',
  'custom-notification',
  'custom-notification-received-user',
  'custom-notification-notification-group',
  'custom-push-notification',
  'custom-push-notification-received-user',
  'custom-push-notification-notification-group',
  'qform',
  'mam-master-account',
  'mam-investor-account',
  'import-item',
  'import-item-record',
  'notification-group',
  'notification-group-member',
  'notification-group-filter',
  'notification-category',
  'document-configuration',
  'document-configuration-item',
  'onboarding-configuration',
  'documents-user-list',
  'notes',
  'payment-system-consent',
  'psp-consent',
  'banner-configuration',
  'video-configuration',
  'video-configuration-link',
  'data-change-request',
  'investor',
  'master',
  'copy-trading-history',
  'user-trading-account-type-limit',
  'trusted-devices',
  'login-history',
  'user-country-access',
  'external-job-crm',
  'crm-entity-cache',
  'sales-user',
  'business-manager',
  'user-psp',
  'user-access',
  'image-file',
  'commission-history-per-ib',
  'auto-notification',
  'sales-activity-history',
  'bank',
  'digital-wallet',
  'user-bank-account',
  'user-crypto-wallet-account',
  'user-digital-wallet-account',
  'user-card',
  'user-name-alias',
  'trading-history-gmi',
  'trading-history-mt4',
  'trading-history-mt5',
  'trading-category',
  'trading-account-type',
  'permission-group',
  'inform-group',
  'event-type-group',
  'user-group',
  'user-group-permission',
  'custom-tag',
  'user-custom-tag',
  'user-custom-tag-crm',
  'marketing-banner-category',
  'marketing-banner-file',
  'trading-symbol',
  'trading-group',
  'trading-symbol-group',
  'trading-bonus',
  'trading-bonus-summary',
  'psp-operation-statistics-reports',
  'components-common',
  'components-psp',
  'components-job',
  'job-history',
  'user-psp-fee',
  'kyt-verification',
  'kyt-verification-operation',
  'kyt-verification-withdrawal-attempt',
  'kyt-verification-api-log',
  'users',
  'user-ui-settings',
] as const;

export type GridContextPersistenceName = (typeof LIST_OF_STORAGES)[number];

type IOrder = string[];
interface IGridPresistanceStorage {
  visibility: VisibilityState | null;
  order: IOrder;
  sizing: ColumnSizingState;
  filters?: {
    visibility: Record<string, boolean>;
    order: string[];
  };
}

class GridContextPersistenceService {
  defaultConfig: IGridPresistanceStorage = { visibility: null, sizing: {}, order: [] };

  constructor() {
    this.purgeNotUsedStorages();
  }

  getKeyPrefix = (key: string) => {
    return `grid.storage.${key}`;
  };

  purgeNotUsedStorages = () => {
    Object.keys(localStorage)
      .filter((key) => key.includes(this.getKeyPrefix('')))
      .forEach((gridStorageKey) => {
        const isExisted = LIST_OF_STORAGES.some((existingStorageKey) => gridStorageKey.includes(existingStorageKey));

        if (!isExisted) {
          localStorage.removeItem(gridStorageKey);
        }
      });
  };

  getGridPersistanceStorage = (key: string): null | IGridPresistanceStorage => {
    const storageKey = this.getKeyPrefix(key);
    const item = localStorage.getItem(storageKey);

    return JSON.parse(item ?? 'null');
  };

  saveVisibilityToGridPersistanceStorage = (key: string, newState: VisibilityState) => {
    const currentConfig = this.getGridPersistanceStorage(key) ?? this.defaultConfig;

    currentConfig['visibility'] = newState;

    localStorage.setItem(this.getKeyPrefix(key), JSON.stringify(currentConfig));
  };

  saveOrderToGridPersistanceStorage = (key: string, newState: IOrder) => {
    const currentConfig = this.getGridPersistanceStorage(key) ?? this.defaultConfig;

    currentConfig['order'] = newState;

    localStorage.setItem(this.getKeyPrefix(key), JSON.stringify(currentConfig));
  };

  saveSizingToGridPersistanceStorage = (key: string, newState: ColumnSizingState) => {
    const currentConfig = this.getGridPersistanceStorage(key) ?? this.defaultConfig;

    currentConfig['sizing'] = newState;

    localStorage.setItem(this.getKeyPrefix(key), JSON.stringify(currentConfig));
  };

  saveFiltersToGridPersistanceStorage = (
    key: string,
    newFilters: { visibility: Record<string, boolean>; order: string[] },
  ) => {
    const currentConfig = this.getGridPersistanceStorage(key) ?? this.defaultConfig;

    currentConfig['filters'] = newFilters;
    localStorage.setItem(this.getKeyPrefix(key), JSON.stringify(currentConfig));
  };

  getFiltersFromGridPersistanceStorage = (
    key: string,
  ): { visibility: Record<string, boolean>; order: string[] } | null => {
    const config = this.getGridPersistanceStorage(key);

    return config && config.filters ? config.filters : null;
  };
}

export const gridContextPersistenceService = new GridContextPersistenceService();
